import { useState, useEffect } from 'react';
import SEO from '@/components/SEO';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ProfessionalCard } from '@/components/ProfessionalCard';
import { ExtendedDirectoryEntry } from '@/types/caregiver';
import { Button } from '@/components/ui/button';
import { ArrowRightIcon } from '@heroicons/react/24/outline';

type DirectoryEntry = ExtendedDirectoryEntry;

const roleTypes = [
  { value: 'all', label: 'All Help', color: 'bg-primary text-primary-foreground' },
  { value: 'caregivers', label: 'Caregivers', color: 'bg-st_light_blue text-white' },
  { value: 'clinical', label: 'Clinical (OT/PT)', color: 'bg-st_taupe text-white' },
  { value: 'specialists', label: 'Specialists', color: 'bg-muted text-muted-foreground' },
  { value: 'legal', label: 'Legal & Financial', color: 'bg-accent text-accent-foreground' },
];

export default function FindHelpPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [selectedRole, setSelectedRole] = useState(searchParams.get('role') || 'all');
  const [entries, setEntries] = useState<DirectoryEntry[]>([]);
  const [loading, setLoading] = useState(false);

  const debouncedSearch = useDebounce(search, 300);

  useEffect(() => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (selectedRole !== 'all') params.set('role', selectedRole);
    setSearchParams(params);
  }, [search, selectedRole, setSearchParams]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        let professionsToLoad: string[] = [];
        
        switch (selectedRole) {
          case 'caregivers':
            professionsToLoad = ['Independent Caregiver'];
            break;
          case 'clinical':
            professionsToLoad = ['OT', 'PT'];
            break;
          case 'specialists':
            professionsToLoad = ['CAPS', 'Senior Care Professional', 'Home Modification Specialist'];
            break;
          case 'legal':
            professionsToLoad = ['Elder Law Attorney', 'Financial Planner'];
            break;
          default:
            professionsToLoad = ['Independent Caregiver', 'OT', 'PT', 'CAPS', 'Senior Care Professional', 'Elder Law Attorney', 'Home Modification Specialist', 'Financial Planner'];
        }

        const promises = professionsToLoad.map(profession => 
          supabase.functions.invoke('get-professional-directory', {
            body: { profession, search: debouncedSearch, limit: 20, offset: 0 },
          })
        );
        
        const results = await Promise.all(promises);
        const allEntries = results.flatMap((result, index) => {
          if (result.error) {
            console.error(`Failed to load ${professionsToLoad[index]} directory:`, result.error);
            return [];
          }
          return (result.data as any)?.professionals ?? [];
        });
        
        setEntries(allEntries);
      } catch (e) {
        console.error('Failed to load directory', e);
        setEntries([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [debouncedSearch, selectedRole]);

  const handleRoleChange = (role: string) => {
    setSelectedRole(role);
  };

  const count = entries.length;

  return (
    <>
      <SEO
        title="Find Help for Aging at Home"
        description="Search across caregivers, professionals, and specialists to find the right support for aging at home."
        canonical="https://seniorthrive.com/find"
      />

      <div className="bg-background min-h-screen">
        <div className="container max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-8">
          <header className="mb-8 text-center">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-3">
              Find Help for Aging at Home
            </h1>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto mb-6">
              Search across our network of caregivers, professionals, and specialists to find the right support.
            </p>
            
            {/* Quick navigation to focused directory */}
            <div className="flex justify-center">
              <Button variant="outline" size="sm" asChild>
                <a href="/professionals/directory" className="inline-flex items-center gap-2">
                  Browse Professional Directory
                  <ArrowRightIcon className="h-4 w-4" />
                </a>
              </Button>
            </div>
          </header>

          <section className="mb-6 space-y-4">
            {/* Search input */}
            <div className="max-w-2xl mx-auto">
              <Input
                placeholder="Search by name, company, or service type"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search all help"
                className="h-12 text-lg"
              />
            </div>

            {/* Role filter chips */}
            <div className="flex flex-wrap gap-2 justify-center">
              {roleTypes.map(role => (
                <Badge
                  key={role.value}
                  variant={selectedRole === role.value ? "default" : "outline"}
                  className={`cursor-pointer px-4 py-2 ${
                    selectedRole === role.value ? role.color : 'hover:bg-muted'
                  }`}
                  onClick={() => handleRoleChange(role.value)}
                >
                  {role.label}
                </Badge>
              ))}
            </div>
          </section>

          {/* Results count */}
          {!loading && (
            <div className="text-center mb-6">
              <p className="text-sm text-muted-foreground" aria-live="polite">
                {count} {count === 1 ? 'result' : 'results'} found
              </p>
            </div>
          )}

          <section className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {loading && (
              <div className="col-span-full text-center text-muted-foreground py-12">
                Searching...
              </div>
            )}
            {!loading && count === 0 && (
              <div className="col-span-full text-center text-muted-foreground py-12">
                <p className="text-lg mb-4">No matches found.</p>
                <p className="text-sm">Try adjusting your search or browse our focused directories above.</p>
              </div>
            )}
            {!loading && entries.map((entry) => (
              <article key={entry.id} className="h-full">
                <ProfessionalCard professional={entry} />
              </article>
            ))}
          </section>
        </div>
      </div>
    </>
  );
}

function useDebounce<T>(value: T, delay = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}